import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BedDouble, ListChecks, Plus, StickyNote, Trash2 } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { storeLabel, type StoreOption } from './InventoryPage';

const INDENTS_TAB = '/inventory?tab=indents';

interface IndentLine {
  key: number;
  productId: string;
  qty: string;
}

let lineSequence = 0;
const nextLineKey = () => ++lineSequence;

export function NewIndentPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), INDENTS_TAB);
  const initialStoreId = params.get('storeId') ?? '';
  const [fromStoreId, setFromStoreId] = useState(initialStoreId);
  const [wardId, setWardId] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<IndentLine[]>([]);
  const [error, setError] = useState<string | null>(null);

  const stores = useQuery({
    queryKey: ['pharmacy-stores'],
    queryFn: () => get<StoreOption[]>('/pharmacy/stores'),
  });
  const wards = useQuery({
    queryKey: ['wards'],
    queryFn: () => get<{ id: string; name: string }[]>('/ipd/wards'),
  });
  const products = useQuery({
    queryKey: ['products'],
    queryFn: () => get<{ id: string; code: string; genericName: string; strength: string | null }[]>('/pharmacy/products?limit=200'),
  });

  const save = useMutation({
    mutationFn: () =>
      post('/pharmacy/indents', {
        fromStoreId,
        wardId: wardId || undefined,
        notes: notes || undefined,
        lines: lines.map((l) => ({ productId: l.productId, qty: Number(l.qty) })),
      }),
    onSuccess: () => {
      toast.success(tr('pharmacy', 'indent_raised'), tr('pharmacy', 'waiting_on_pharmacy_approval'));
      void queryClient.invalidateQueries({ queryKey: ['indents'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const complete = Boolean(fromStoreId) && lines.length > 0 && lines.every((l) => l.productId && Number(l.qty) > 0);
  const dirty = !save.isSuccess && (fromStoreId !== initialStoreId || wardId !== '' || notes !== '' || lines.length > 0);

  return (
    <FormScreen
      title={tr('pharmacy', 'raise_a_ward_indent')}
      description={tr('pharmacy', 'requests_stock_from_a_pharmacy_store_pharmacy')}
      eyebrow={tr('pharmacy', 'inventory')}
      icon={BedDouble}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!complete) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('admin', 'request')} icon={BedDouble}>
        <Field label={tr('pharmacy', 'from_store')} htmlFor="indent-store" required>
          <Select id="indent-store" value={fromStoreId} onChange={(e) => setFromStoreId(e.target.value)}>
            <option value="">{tr('pharmacy', 'select')}</option>
            {stores.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {storeLabel(s)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('pharmacy', 'ward')} htmlFor="indent-ward">
          <Select id="indent-ward" value={wardId} onChange={(e) => setWardId(e.target.value)}>
            <option value="">—</option>
            {wards.data?.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>

      <FormSection title={tr('pharmacy', 'items')} icon={ListChecks} columns={1}>
        {lines.map((line, i) => (
          <div key={line.key} className="grid grid-cols-[1fr_100px_32px] gap-2">
            <Select value={line.productId} onChange={(e) => setLines(lines.map((l, j) => (j === i ? { ...l, productId: e.target.value } : l)))}>
              <option value="">{tr('pharmacy', 'product_2')}</option>
              {products.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.genericName} {p.strength ?? ''} ({p.code})
                </option>
              ))}
            </Select>
            <Input
              type="number"
              min={0}
              placeholder={tr('pharmacy', 'qty_2')}
              value={line.qty}
              onChange={(e) => setLines(lines.map((l, j) => (j === i ? { ...l, qty: e.target.value } : l)))}
            />
            <Button type="button" variant="ghost" size="icon" aria-label={tr('pharmacy', 'remove')} onClick={() => setLines(lines.filter((_, j) => j !== i))}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => setLines([...lines, { key: nextLineKey(), productId: '', qty: '' }])}>
            <Plus className="h-4 w-4" /> {tr('pharmacy', 'add_line')}
          </Button>
        </div>
      </FormSection>

      <FormSection title={tr('pharmacy', 'notes')} icon={StickyNote} columns={1}>
        <Field label={tr('pharmacy', 'notes')} htmlFor="indent-notes">
          <Textarea id="indent-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('pharmacy', 'raise_indent')} loading={save.isPending} incomplete={!complete} cancelTo={returnTo} />
    </FormScreen>
  );
}
